import { request } from './api';
import { setFilmGrant } from './productionGrant';

export type GuildCurrency = 'INR' | 'USD';
export type GuildPipeline = 'film' | 'vfx' | 'marketing' | 'saas' | 'upscale';

export interface GuildMilestoneTemplate { title: string; share: number; deliverables: string; acceptance: string }
export interface GuildDiscipline {
  id: string;
  label: string;
  group: string;
  pipeline: GuildPipeline;
  pipelineLabel: string;
  unit: string;
  formats: string[];
  skills: string[];
  milestones: GuildMilestoneTemplate[];
  filmType: string | null;
  saasMode: 'walkthrough' | 'showcase' | 'hybrid' | null;
  look: '2d' | '3d' | null;
}
export interface GuildCatalog {
  groups: string[];
  pipelines: { id: GuildPipeline; label: string; projectKind: string; engine: string }[];
  currencies: GuildCurrency[];
  disciplines: GuildDiscipline[];
  notice: string;
  defaults: { autoApproveDays: number; paymentDueDays: number; revisionsPerMilestone: number; readOnlyDays: number };
}

export interface GuildMilestonePlan {
  key?: string;
  title: string;
  deliverables: string;
  acceptance: string;
  amount: number;
  dueAt?: string | null;
  dueInDays?: number | null;
  revisions: number;
}

export interface GuildBudget { type: 'fixed' | 'range'; currency: GuildCurrency; min: number; max: number }
export interface GuildComputeEstimate { lowInr: number; highInr: number; basis: string; lines?: { discipline: string; label: string; units: string; lowInr: number; highInr: number }[] }

export interface GuildJobSummary {
  id: string;
  status: string;
  title: string;
  disciplines: string[];
  pipeline: GuildPipeline;
  budget: GuildBudget;
  deadline: string | null;
  visibility: 'public' | 'invite' | 'both';
  formats: string[];
  durationSeconds: number;
  quantity: number;
  clientName: string;
  clientVerified: boolean;
  bidCount: number;
  ndaRequired: boolean;
  createdAt: string;
  openedAt: string | null;
  summary: string;
  version?: number;
  newBids?: number;
  invited?: boolean;
  match?: { score: number; reasons: string[] };
}

export interface GuildJobDetail extends GuildJobSummary {
  deliverables: { title: string; acceptance: string; format: string }[];
  aspectRatio: string;
  milestonesMode: 'proposed' | 'bids';
  milestones: GuildMilestonePlan[];
  compute: { fundedBy: 'client'; ceilingInr: number; estimate: GuildComputeEstimate | null };
  screening: string[];
  testTask: { enabled: boolean; description: string; feeNote: string };
  paymentTerms: string;
  paymentDueDays: number;
  autoApproveDays: number;
  revisionsPerMilestone: number;
  ipTerms: string;
  portfolioUse: { allowed: boolean; releaseAt: string | null };
  craftSkills: string[];
  nda: { required: boolean; text: string };
  privateBrief: string;
  privateBriefLocked: boolean;
  invites?: string[];
}

export interface GuildPortfolioItem {
  id: string;
  kind: 'frameon' | 'external';
  title: string;
  discipline: string;
  description: string;
  url: string;
  mediaId: string;
  projectId: string;
  verified: boolean;
  provenance: { model: string; prompt: string; mime: string; createdAt: string; madeOnFrameon: boolean } | null;
}

export interface GuildStats { jobsCompleted?: number; activeContracts?: number; onTimeRate?: number | null; rating?: number | null; ratingCount?: number }

export interface GuildProfile {
  userId: string;
  handle: string;
  displayName: string;
  location: string;
  timezone: string;
  languages: string[];
  bio: string;
  disciplines: string[];
  specialties: string[];
  models: string[];
  rates: { currency: GuildCurrency; hourly: number; project: number };
  availability: 'open' | 'busy' | 'away';
  paymentMethods: string;
  portfolio: GuildPortfolioItem[];
  agency: boolean;
  verification: { email: boolean; phone: boolean };
  suspended: boolean;
  stats: GuildStats | null;
  version?: number;
  updatedAt: string;
}

export interface GuildOffer {
  price: number;
  currency: GuildCurrency;
  timelineDays: number;
  milestones: GuildMilestonePlan[];
  paymentTerms: string;
  paymentDueDays: number;
  autoApproveDays: number;
  ipTerms: string;
  ndaRequired: boolean;
  portfolioUse: { allowed: boolean; releaseAt: string | null };
  computeCeilingInr: number;
  note: string;
  revision?: number;
  at?: string;
}

export interface GuildBid {
  id: string;
  jobId: string;
  artistId: string;
  status: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  price: number;
  currency: GuildCurrency;
  timelineDays: number;
  milestones: GuildMilestonePlan[];
  coverNote: string;
  answers: { question: string; answer: string }[];
  portfolioIds: string[];
  computeEstimate: { approach: string; lowInr: number; highInr: number };
  artistName: string;
  artistHandle: string;
  edited?: boolean;
  offer?: GuildOffer | null;
  counter?: (GuildOffer & { offerRevision: number }) | null;
  declineReason?: string;
  artist?: GuildProfile;
  job?: GuildJobSummary | null;
}

export interface GuildPayment {
  markedPaidAt: string | null;
  paidOn: string | null;
  method: string;
  reference: string;
  receivedAt: string | null;
  notReceived: { at: string; note: string }[];
  reminders: { at: string }[];
}

export interface GuildSubmission { id: string; at: string; versions: { mediaId: string; label: string }[]; cutVersionId: string; note: string; presentationId: string | null; reviewItemIds: string[] }
export interface GuildDecision { type: 'approved' | 'auto-approved' | 'changes-requested'; at: string; note: string }

export interface GuildMilestone {
  id: string;
  title: string;
  deliverables: string;
  acceptance: string;
  amount: number;
  dueAt: string;
  revisionsAllowed: number;
  revisionsLeft: number;
  status: 'planned' | 'in-progress' | 'submitted' | 'changes-requested' | 'approved' | 'cancelled';
  submissions: GuildSubmission[];
  decisions: GuildDecision[];
  approvedAt: string | null;
  payment: GuildPayment;
  paymentState: 'not-due' | 'awaiting-payment' | 'overdue' | 'marked-paid' | 'received';
  paymentDueAt: string | null;
  autoApproveAt: string | null;
  late: boolean;
  daysLate: number;
}

export interface GuildChange { type: 'add-milestone' | 'remove-milestone' | 'update-milestone' | 'update-terms'; milestone?: GuildMilestonePlan; milestoneId?: string; fields?: Record<string, unknown> }
export interface GuildChangeOrder { id: string; proposedBy: 'client' | 'artist'; at: string; note: string; changes: GuildChange[]; status: 'pending' | 'accepted' | 'declined' | 'withdrawn' }
export interface GuildFlag { id: string; milestoneId: string | null; by: 'client' | 'artist'; kind: 'work' | 'payment' | 'other'; note: string; at: string; clearedAt: string | null }
export interface GuildPaymentSummary { currency: GuildCurrency; agreed: number; approved: number; markedPaid: number; received: number; overdue: number; overdueCount: number }

export interface GuildContract {
  id: string;
  jobId: string;
  bidId: string;
  clientOrgId: string;
  clientName: string;
  artistId: string;
  artistName: string;
  artistHandle: string;
  status: 'active' | 'completed' | 'cancelled';
  role: 'artist' | 'client' | 'client-team';
  createdAt: string;
  completedAt: string | null;
  terms: {
    revision: number;
    hash: string;
    title: string;
    scope: string;
    deliverables: { title: string; acceptance: string; format: string }[];
    disciplines: string[];
    pipeline: GuildPipeline;
    craftSkills: string[];
    formats: string[];
    currency: GuildCurrency;
    price: number;
    timelineDays: number;
    paymentTerms: string;
    paymentDueDays: number;
    autoApproveDays: number;
    ndaRequired: boolean;
    ipTerms: string;
    portfolioUse: { allowed: boolean; releaseAt: string | null };
    compute: { fundedBy: 'client'; ceilingInr: number };
    notAParty: string;
  };
  milestones: GuildMilestone[];
  changeOrders: GuildChangeOrder[];
  flags: GuildFlag[];
  workspace: { projectId: string; projectName: string; projectKind: string; attachedAt: string; grantId: string | null; role: string } | null;
  access: { readOnlyFrom: string | null; revokeAt: string | null; revokedAt: string | null };
  paymentSummary: GuildPaymentSummary;
  compute: { ceilingInr: number; usedInr: number; remainingInr: number; maxJobInr: number; status: string; expiresAt: string } | null;
  reviews: { id: string; party: 'client' | 'artist'; rating: number; text: string; at: string }[];
  reviewedByMe: boolean;
  reviewsRevealAt: string | null;
  canReview: boolean;
  notice: string;
}

export interface GuildContractRow {
  id: string;
  role: 'artist' | 'client';
  status: string;
  title: string;
  clientName: string;
  artistName: string;
  currency: GuildCurrency;
  price: number;
  workspace: GuildContract['workspace'];
  paymentSummary: GuildPaymentSummary;
  nextDueAt: string | null;
  waitingOnClient: number;
  createdAt: string;
}

export interface GuildMessage { id: string; authorId: string; mine: boolean; at: string; body: string; attachments: { name: string; url?: string; mediaId?: string }[]; party: 'client' | 'artist'; authorName: string; signals: { id: string; label: string }[] }

export interface GuildMe {
  profile: GuildProfile | null;
  org: { id: string; name: string; role: string };
  guest: boolean;
  canPostJobs: boolean;
  platformAdmin: boolean;
  notice: string;
}

export interface GuildWorkspaceContract {
  id: string;
  status: string;
  title: string;
  role: 'artist' | 'client' | 'client-team';
  artistName: string;
  clientName: string;
  craftSkills: string[];
  disciplines: string[];
  readOnly: boolean;
  nextDue: { id: string; title: string; status: string; dueAt: string } | null;
  milestones: { id: string; title: string; status: string; dueAt: string; paymentState: string }[];
}

export interface GuildJobInput {
  title: string;
  summary: string;
  disciplines: string[];
  deliverables: { title: string; acceptance: string; format: string }[];
  formats: string[];
  durationSeconds: number;
  quantity: number;
  aspectRatio: string;
  deadline: string | null;
  budget: GuildBudget;
  milestones: GuildMilestonePlan[];
  compute: { ceilingInr: number; estimate: GuildComputeEstimate | null };
  visibility: 'public' | 'invite' | 'both';
  nda: { required: boolean; text: string };
  privateBrief: string;
  screening: string[];
  testTask: { enabled: boolean; description: string; feeNote: string };
  paymentTerms: string;
  paymentDueDays: number;
  autoApproveDays: number;
  revisionsPerMilestone: number;
  ipTerms: string;
  portfolioUse: { allowed: boolean; releaseAt: string | null };
}

export interface GuildBidInput {
  price: number;
  timelineDays: number;
  milestones: GuildMilestonePlan[];
  coverNote: string;
  answers: string[];
  portfolioIds: string[];
  computeEstimate: { approach: string; lowInr: number; highInr: number };
}

export interface GuildBriefDraft {
  title: string;
  summary: string;
  disciplines: string[];
  pipeline: GuildPipeline;
  craftSkills: string[];
  deliverables: { title: string; acceptance: string; format: string }[];
  formats: string[];
  milestones: GuildMilestonePlan[];
  compute: GuildComputeEstimate | null;
}

const enc = encodeURIComponent;
const json = (method: string, body?: unknown): RequestInit => ({ method, body: JSON.stringify(body ?? {}) });

export const guildApi = {
  catalog: () => request<GuildCatalog>('/api/guild/catalog'),
  me: () => request<GuildMe>('/api/guild/me'),
  saveProfile: (profile: Partial<GuildProfile> & { version?: number }) => request<{ profile: GuildProfile }>('/api/guild/profile', json('PUT', profile)),
  portfolioSources: () => request<{ items: { mediaId: string; projectId: string; projectName: string; name: string; mime: string }[] }>('/api/guild/portfolio-sources'),
  artists: (query: Record<string, string> = {}) => request<{ artists: GuildProfile[] }>(`/api/guild/artists?${new URLSearchParams(query)}`),
  artist: (handle: string) => request<{ artist: GuildProfile; reviews: { rating: number; text: string; at: string; jobTitle: string; clientName: string }[] }>(`/api/guild/artists/${enc(handle)}`),
  board: (query: Record<string, string> = {}) => request<{ jobs: GuildJobSummary[] }>(`/api/guild/jobs?${new URLSearchParams({ scope: 'board', ...query })}`),
  myJobs: () => request<{ jobs: GuildJobSummary[] }>('/api/guild/jobs?scope=mine'),
  myBids: () => request<{ bids: GuildBid[] }>('/api/guild/jobs?scope=bids'),
  createJob: (job: GuildJobInput, publish: boolean) => request<{ job: GuildJobDetail }>('/api/guild/jobs', json('POST', { job, publish })),
  updateJob: (jobId: string, job: GuildJobInput, version: number) => request<{ job: GuildJobDetail }>(`/api/guild/jobs/${enc(jobId)}`, json('PATCH', { job, version })),
  job: (jobId: string) => request<{ job: GuildJobDetail; owner: boolean; canManage: boolean; clientStats: Record<string, number | null>; bids?: GuildBid[]; contracts?: { id: string; status: string; artistName: string; price: number; currency: GuildCurrency }[]; myBid?: GuildBid | null; match?: { score: number; reasons: string[] }; hasProfile?: boolean }>(`/api/guild/jobs/${enc(jobId)}`),
  jobStatus: (jobId: string, action: string, version: number) => request<{ job: GuildJobDetail }>(`/api/guild/jobs/${enc(jobId)}/status`, json('POST', { action, version })),
  invite: (jobId: string, handle: string) => request<{ invites: number }>(`/api/guild/jobs/${enc(jobId)}/invite`, json('POST', { handle })),
  acceptNda: (jobId: string) => request<{ accepted: boolean }>(`/api/guild/jobs/${enc(jobId)}/nda`, json('POST', { accept: true })),
  bidSummary: (jobId: string) => request<{ rows: { bidId: string; artistName: string; price: number; timelineDays: number; assumes: string[]; missing: string[] }[]; advisory: string }>(`/api/guild/jobs/${enc(jobId)}/summary`),
  placeBid: (jobId: string, bid: GuildBidInput, submit = true) => request<{ bid: GuildBid }>(`/api/guild/jobs/${enc(jobId)}/bids`, json('POST', { bid, submit })),
  editBid: (bidId: string, bid: GuildBidInput, version: number) => request<{ bid: GuildBid }>(`/api/guild/bids/${enc(bidId)}`, json('PATCH', { bid, version })),
  bidAction: (bidId: string, action: 'withdraw' | 'shortlist' | 'decline' | 'decline-offer', body: Record<string, unknown> = {}) => request<{ bid: GuildBid }>(`/api/guild/bids/${enc(bidId)}/${action}`, json('POST', body)),
  offer: (bidId: string, offer: Partial<GuildOffer>) => request<{ bid: GuildBid }>(`/api/guild/bids/${enc(bidId)}/offer`, json('POST', { offer })),
  counter: (bidId: string, offer: Partial<GuildOffer>) => request<{ bid: GuildBid }>(`/api/guild/bids/${enc(bidId)}/counter`, json('POST', { offer })),
  acceptOffer: (bidId: string, offerRevision: number) => request<{ contract: GuildContract }>(`/api/guild/bids/${enc(bidId)}/accept`, json('POST', { offerRevision, acknowledged: true })),
  contracts: () => request<{ contracts: GuildContractRow[] }>('/api/guild/contracts'),
  contract: (contractId: string) => request<{ contract: GuildContract }>(`/api/guild/contracts/${enc(contractId)}`),
  attachWorkspace: (contractId: string, input: { projectId: string; computeCeilingInr: number; maxJobInr?: number }) => request<{ contract: GuildContract }>(`/api/guild/contracts/${enc(contractId)}/workspace`, json('POST', input)),
  enterWorkspace: (contractId: string) => request<{ path: string; readOnly: boolean }>(`/api/guild/contracts/${enc(contractId)}/enter`, json('POST')),
  milestone: (contractId: string, milestoneId: string, action: string, body: Record<string, unknown> = {}) => request<{ contract: GuildContract }>(`/api/guild/contracts/${enc(contractId)}/milestones/${enc(milestoneId)}/${action}`, json('POST', body)),
  proposeChange: (contractId: string, changes: GuildChange[], note: string) => request<{ contract: GuildContract }>(`/api/guild/contracts/${enc(contractId)}/change-orders`, json('POST', { changes, note })),
  decideChange: (contractId: string, orderId: string, decision: 'accept' | 'decline' | 'withdraw') => request<{ contract: GuildContract }>(`/api/guild/contracts/${enc(contractId)}/change-orders/${enc(orderId)}/${decision}`, json('POST')),
  flag: (contractId: string, kind: string, note: string) => request<{ contract: GuildContract }>(`/api/guild/contracts/${enc(contractId)}/flags`, json('POST', { kind, note })),
  clearFlag: (contractId: string, flagId: string) => request<{ contract: GuildContract }>(`/api/guild/contracts/${enc(contractId)}/flags/${enc(flagId)}/clear`, json('POST')),
  cancel: (contractId: string, reason: string) => request<{ contract: GuildContract }>(`/api/guild/contracts/${enc(contractId)}/cancel`, json('POST', { reason })),
  review: (contractId: string, rating: number, text: string) => request<{ contract: GuildContract }>('/api/guild/reviews', json('POST', { contractId, rating, text })),
  report: (targetKind: string, targetId: string, reason: string) => request<{ ok: true }>('/api/guild/reports', json('POST', { targetKind, targetId, reason })),
  messages: (threadId: string) => request<{ messages: GuildMessage[]; notice: string }>(`/api/guild/threads/${enc(threadId)}/messages`),
  sendMessage: (threadId: string, body: string) => request<{ message: GuildMessage }>(`/api/guild/threads/${enc(threadId)}/messages`, json('POST', { body })),
  draftBrief: (input: { idea: string; disciplines: string[]; durationSeconds?: number; quantity?: number; budget?: number; deadline?: string | null; currency?: GuildCurrency }) => request<{ brief: GuildBriefDraft }>('/api/guild/assist/brief', json('POST', input)),
  suggestMilestones: (input: { disciplines: string[]; budget: number; deadline?: string | null }) => request<{ milestones: GuildMilestonePlan[] }>('/api/guild/assist/milestones', json('POST', input)),
  estimateCompute: (input: { disciplines: string[]; durationSeconds?: number; quantity?: number; revisions?: number }) => request<{ estimate: GuildComputeEstimate | null }>('/api/guild/assist/compute', json('POST', input)),
  workspace: (projectId: string) => request<{ contract: GuildWorkspaceContract | null }>(`/api/projects/${enc(projectId)}/guild`),
  switchStudio: (studioId: string) => request<{ ok: true }>('/api/account/studio', json('POST', { studioId })),
  workspaceMedia: (contractId: string) => request<{ media: { id: string; name: string; mime: string; kind: string; createdAt: string }[] }>(`/api/guild/contracts/${enc(contractId)}/workspace-media`),
  evidenceUrl: (contractId: string, format: 'html' | 'json' = 'html') => `/api/guild/contracts/${enc(contractId)}/evidence${format === 'json' ? '?format=json' : ''}`
};

const CRAFT_NAMES: Record<string, string> = {
  'lottie-motion/motion-design': 'Motion direction (Lottie)', 'genjutsu/motion-principles': 'Motion principles', 'genjutsu/tells': 'Avoiding the generic AI look',
  'gsap/gsap-timeline': 'GSAP timelines', 'gsap/gsap-core': 'GSAP easing', 'design-dna/design-dna': 'Brand design DNA', 'motion-graphics/motion-broll': 'Motion b-roll',
  'footage-edit/reel-editor': 'Reel editing', 'film-craft/screenwriter': 'Screenwriting', 'film-craft/shotlist-builder': 'Shot lists', 'film-craft/seedance-clean': 'Seedance prompting',
  'film-craft/ai-art-director': 'Art direction', 'film-craft/banana-pro-director': 'Character and image direction', 'film-craft/cinema-worldbuilder-pro': 'Worldbuilding'
};

export function craftLabel(skill: string): string {
  if (CRAFT_NAMES[skill]) return CRAFT_NAMES[skill];
  const name = skill.split('/').pop() || skill;
  const words = name.replace(/^hyperframes-?/, 'HyperFrames ').replace(/-/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export const CURRENCY_SYMBOL: Record<GuildCurrency, string> = { INR: '₹', USD: '$' };

export function money(value: number, currency: GuildCurrency = 'INR'): string {
  return `${CURRENCY_SYMBOL[currency] || ''}${Math.round(Number(value) || 0).toLocaleString(currency === 'INR' ? 'en-IN' : 'en-US')}`;
}

export function budgetLabel(budget: GuildBudget): string {
  return budget.type === 'range' && budget.max !== budget.min ? `${money(budget.min, budget.currency)}–${money(budget.max, budget.currency)}` : money(budget.max, budget.currency);
}

export function shortDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function relativeDays(value: string | null | undefined): string {
  if (!value) return '';
  const days = Math.round((Date.parse(value) - Date.now()) / 86400000);
  if (days === 0) return 'today';
  return days > 0 ? `in ${days} day${days === 1 ? '' : 's'}` : `${-days} day${days === -1 ? '' : 's'} ago`;
}

export const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', open: 'Open', paused: 'Paused', closed: 'Hiring closed', 'in-contract': 'In contract', completed: 'Completed', cancelled: 'Cancelled',
  submitted: 'Submitted', shortlisted: 'Shortlisted', offered: 'Offer sent', countered: 'Countered', accepted: 'Hired', declined: 'Declined', withdrawn: 'Withdrawn', expired: 'Expired',
  active: 'Active', planned: 'Planned', 'in-progress': 'In progress', 'changes-requested': 'Changes requested', approved: 'Approved',
  'not-due': 'Not due', 'awaiting-payment': 'Awaiting payment', overdue: 'Overdue', 'marked-paid': 'Marked paid', received: 'Received'
};

export function statusTone(status: string): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  if (['approved', 'received', 'completed', 'accepted', 'active', 'open'].includes(status)) return 'success';
  if (['overdue', 'declined', 'cancelled', 'withdrawn', 'expired'].includes(status)) return 'danger';
  if (['changes-requested', 'awaiting-payment', 'countered', 'paused', 'marked-paid', 'submitted', 'offered'].includes(status)) return 'warning';
  if (['shortlisted', 'in-progress', 'in-contract'].includes(status)) return 'info';
  return 'neutral';
}

export interface GuildWorkspaceInfo { contract: GuildWorkspaceContract; compute: { ceilingInr: number; remainingInr: number; status: string } | null }

export async function loadGuildWorkspace(project: { id: string; guild?: unknown; projectKind?: string }): Promise<GuildWorkspaceInfo | null> {
  if (!project.guild) return null;
  const [result, plan] = await Promise.all([
    guildApi.workspace(project.id).catch(() => ({ contract: null })),
    project.projectKind === 'marketing' ? null : request<{ plan: { id: string; status: string; ceilingInr: number; remainingInr: number } | null }>(`/api/projects/${enc(project.id)}/plan`).catch(() => ({ plan: null }))
  ]);
  if (plan) setFilmGrant(project.id, plan.plan?.status === 'active' ? plan.plan.id : null);
  if (!result.contract) return null;
  return { contract: result.contract, compute: plan?.plan ? { ceilingInr: plan.plan.ceilingInr, remainingInr: plan.plan.remainingInr, status: plan.plan.status } : null };
}
